<template>
  <div style="display: flex">
    <div style="font-weight: bold; font-size: 20px">
      <v-icon class="mr-3">mdi-card-text-outline</v-icon>
      Your Canvas
    </div>
    <v-spacer />
    <HelpButton mdxName="YourCanvasPro" />
  </div>
  <v-divider class="mt-3" />
  <div class="config-page-body">
    <div style="margin-top: 40px; margin-bottom: 20px">
      <v-card-subtitle>CanvasPro License</v-card-subtitle>
      <v-divider />
    </div>
    <div
      class="mb-5"
      style="
        background: var(--v-backgroundColor-base);
        padding: 0px;
        border-radius: 4px;
        overflow: auto;
        display: flex;
        align-content: start;
        flex-direction: column;
      ">
      <v-card
        class="pa-3"
        flat
        style="flex-grow: 1">
        <div style="display: flex; height: 88px">
          <div
            class="mt-3"
            style="background: var(--main-bg-color)"
            id="logolarge"
            v-if="licenseInfo?.product_name?.toLowerCase() === 'omni'">
            <img
              :src="omniSvg"
              style="height: 50px" />
          </div>

          <div
            class="mt-3"
            style="background: var(--main-bg-color)"
            id="logolarge"
            v-else>
            <img
              :src="oneSvg"
              style="height: 50px" />
          </div>

          <v-card flat>
            <v-card-title
              v-if="licenseInfo?.product_name"
              style="display: flex; font-weight: lighter; padding-bottom: 0px">
              {{ 'CanvasPro ' + toTitleCase(licenseInfo?.product_name) + '' }}
            </v-card-title>
            <div
              class="ml-1 mb-0 pb-0"
              style="
                color: #5d6ae3;
                font-weight: bold !important;
                text-transform: uppercase;
                font-size: 16px;
                padding-left: 12px !important;
                letter-spacing: 1px;
              ">
              {{ toTitleCase(licenseInfo?.product_edition) }}
            </div>
            <v-card-subtitle
              v-if="licenseInfo?.licensed_to"
              class="mt-0"
              :title="licenseInfo?.licensed_to_email">
              {{ 'For ' + licenseInfo?.licensed_to }}
            </v-card-subtitle>
          </v-card>
        </div>
        <v-hover>
          <template v-slot:default="{ isHovering, props }">
            <v-progress-linear
              :rotate="0"
              :size="280"
              :width="20"
              v-bind="props"
              :indeterminate="licenseInfo.license_percentage === undefined"
              v-model="licenseInfo.license_percentage"
              color="primary"
              class="progress-wheel my-3"
              style="
                background-color: #393a38;
                height: 50px !important;
                border-radius: 0px;
                color: white;
                font-size: 20px;
                overflow: hidden;
                border-radius: 5px;
                font-family: 'Montserrat' !important;
                font-weight: bolder;
              ">
              <div v-if="licenseInfo?.expiry_date_as_str">
                <div v-if="!isHovering">
                  License Expires in
                  {{ licenseInfo?.license_days_remaining }}
                  Days
                </div>
                <div v-else>
                  Expire Date: {{ licenseInfo?.expiry_date_as_str }}
                </div>
              </div>
              <div v-else>Loading License Information...</div>
            </v-progress-linear>

            <Entitlements
              :entitlements="entitlements"
              v-if="entitlements" />
          </template>
        </v-hover>
      </v-card>
    </div>
    <div style="margin-top: 40px; margin-bottom: 20px">
      <v-card-subtitle>Details</v-card-subtitle>
      <v-divider />
    </div>
    <v-text-field
      v-on:keyup.enter="update_config_details()"
      filled
      background-color="textBoxBackground"
      label="Canvas Name"
      hint="A way to identifiy your CanvasPro"
      persistent-hint
      v-model="name"
      class="my-3"
      @input="edited = true"></v-text-field>
    <v-text-field
      v-on:keyup.enter="update_config_details()"
      filled
      background-color="textBoxBackground"
      label="Canvas Description"
      hint="Any additional infomation one might want to know about this machine"
      persistent-hint
      class="my-3"
      @input="edited = true"
      v-model="description"></v-text-field>
    <!-- Canvas Area moved to Frames page (FrameConfig.vue) -->
    <v-divider
      class="mt-3"
      style="margin-bottom: 12px" />
    <div style="display: flex">
      <v-spacer />
      <v-btn
        :disabled="!edited"
        color="primary"
        @click="update_config_details()"
        :loading="update_frames_loading"
        variant="flat">
        <v-icon class="ml-1 mr-2">mdi-content-save</v-icon>
        Save
      </v-btn>
    </div>
  </div>
</template>
<script setup>
import { onMounted, ref, watch, computed } from 'vue';
import { deluge } from '../../stores/deluge.js';
import { $http } from '../../stores/http.js';
import HelpButton from '../Help-Hints/HelpButton.vue';
import Entitlements from './Entitlements.vue';
import omniSvg from '../../assets/OmniLight.svg';
import oneSvg from '../../assets/OneLight.svg';

const licenseInfo = ref({});
const name = ref('');
const description = ref('');
const canvas_height = ref(1080);
const canvas_width = ref(1920);
const total_pixels = ref(0);
const user_canvas_height = ref(0);
const user_canvas_width = ref(0);
const update_frames_loading = ref(false);

const entitlements = computed(() =>
  licenseInfo.value?.entitlements?.length > 0
    ? licenseInfo.value.entitlements
    : undefined
);

const edited = computed(() => {
  if (!deluge.config) return false;
  return (
    name.value !== deluge.config.name ||
    description.value !== deluge.config.description ||
    user_canvas_width.value !== deluge.config.canvas.x ||
    user_canvas_height.value !== deluge.config.canvas.y
  );
});

async function initializeData() {
  licenseInfo.value = JSON.parse(await $http('GET', '/license_info'));

  if (!deluge.config) return;

  name.value = deluge.config.name;
  description.value = deluge.config.description;
  canvas_width.value = deluge.config.canvas.x;
  canvas_height.value = deluge.config.canvas.y;
  user_canvas_width.value = deluge.config.canvas.x;
  user_canvas_height.value = deluge.config.canvas.y;
  total_pixels.value = deluge.config.canvas_px_size;
}

onMounted(() => {
  initializeData();
});

watch(
  () => deluge.config,
  () => {
    if (update_frames_loading.value) return;
    initializeData();
  }
);

function isOmniProduct() {
  return deluge.canvaspro_core_info?.product_name?.toLowerCase() === 'omni';
}

async function canvas_update() {
  try {
    await $http(
      'PUT',
      '/dynamic_engine/update_frames',
      JSON.stringify({
        canvas_height: user_canvas_height.value,
        canvas_width: user_canvas_width.value,
        frames: [],
      })
    );
  } catch (e) {
    console.log(e);
  }
}

function toTitleCase(str) {
  if (!str) return str;
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

async function update_config_details() {
  update_frames_loading.value = true;
  if (!description.value) {
    description.value = '';
  }

  try {
    if (
      !(user_canvas_width.value * user_canvas_height.value > total_pixels.value)
    ) {
      await canvas_update();
    } else {
      alert(
        'Settings are invalid as resolution exceeds total pixels available to current license.'
      );
    }
    await $http(
      'PUT',
      '/update_config_details?name=' +
        name.value +
        '&description=' +
        description.value
    );
  } catch (error) {
    console.log(error);
  }
  update_frames_loading.value = false;
}

async function download(url, file_name) {
  try {
    const data = await $http('GET', url);
    const element = document.createElement('a');
    element.setAttribute(
      'href',
      'data:text/plain;charset=utf-8,' + encodeURIComponent(data)
    );
    element.setAttribute(
      'download',
      file_name + ' ' + new Date().toISOString() + '.txt'
    );
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  } catch (error) {
    console.log(error);
  }
}
</script>
<style scoped>
@import './config.css';
</style>
